import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileWithPath, useDropzone } from 'react-dropzone';

import styles from './styles/FileUpload.module.scss';

import Button from '../Common/Button';

// Types
import { ContactInfo } from '../../types/contactInfo';

type FileUploadProps = {
  form: {
    form: ContactInfo;
    setForm: (value: React.SetStateAction<ContactInfo>) => void;
  };
};

type Attachment = {
  name: string;
  content: string;
};

const FileUpload: React.FunctionComponent<FileUploadProps> = (props: FileUploadProps) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const { form, setForm } = props.form;
  const [fileAttachments, setFileAttachments] = useState<Attachment[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file: File) => {
        const attachments = fileAttachments;
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function () {
          const content = typeof reader.result === 'string' ? reader.result.split(',').pop() : undefined;

          if (content) {
            const attachment = {
              name: file.name,
              content: content,
            };
            attachments.push(attachment);
            setFileAttachments(attachments);

            const newForm: ContactInfo = { ...form };
            newForm['fileAttachments'] = JSON.stringify(attachments);
            setForm(newForm);
          }
        };
      });
      setFiles([...files, ...acceptedFiles]);
    },
    [files, fileAttachments, form, setForm],
  );

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    noClick: files.length > 0,
  });

  const removeFile = (file: File) => () => {
    const newFiles = [...files];
    const newAttachments = [...fileAttachments];
    const start = newFiles.indexOf(file);

    newFiles.splice(start, 1);
    newAttachments.splice(start, 1);

    const newForm: ContactInfo = { ...form };
    if (newAttachments.length > 0) {
      newForm['fileAttachments'] = JSON.stringify(newAttachments);
    } else {
      delete newForm['fileAttachments'];
    }

    setFiles(newFiles);
    setFileAttachments(newAttachments);
    setForm(newForm);
  };

  const removeAll = () => {
    setFiles([]);
    setFileAttachments([]);
    const newForm: ContactInfo = { ...form };
    delete newForm['fileAttachments'];
    setForm(newForm);
  };

  const fileList = files.map((file: FileWithPath) => (
    <li key={file.path}>
      <span>{file.path}</span>
      <button onClick={removeFile(file)}>{t('common:general.deleteButton')}</button>
    </li>
  ));

  return (
    <div className={styles.attachments}>
      <label>
        {t('customerService:contactUs.attachments')} {t('customerService:contactUs.optionalField')}
      </label>
      <Button type="button" className="buttonWhite" onClick={(): void => open()}>
        {t('customerService:contactUs.selectFiles')}
      </Button>
      {fileList.length > 0 && (
        <Button type="button" className="buttonWhite" onClick={(): void => removeAll()}>
          {t('customerService:contactUs.resetButton')}
        </Button>
      )}
      <div {...getRootProps({ className: styles.dropzone })}>
        <input {...getInputProps()} />
        {fileList.length === 0 && <p>{t('customerService:contactUs.dragDropArea')}</p>}
        {fileList.length > 0 && <ul>{fileList}</ul>}
      </div>
    </div>
  );
};

export default FileUpload;
